"use client";

import Image from "next/image";
import Link from "@/components/ui/Link";
import { useRouter } from "next/navigation";
import { MessageIcon } from "../ui/message-icon";
import { BOOKMARK_CATEGORIES } from "@/lib/api/public-client";
import { FALLBACK_IMAGE } from '@/lib/constants';
import { getImageUrl, getTimeAgo, getSectionUrl, getFocusPointStyle, ImageMetadata, BLUR_PLACEHOLDER } from '@/lib/componentutills';

type FieldConfig = {
  hidden?: boolean;
  value?: string;
  customClass?: string;
};

type Props = {
  category?: FieldConfig;
  sectionUrl?: string;
  title?: FieldConfig;
  timeAgo?: FieldConfig;
  imageSrc?: FieldConfig;
  slug?: string;
  priority?: boolean;
  imageMetadata?: ImageMetadata;
  templatetype?: FieldConfig;
};

export const VerticalSmallImageCategoryNews = ({
  category = { hidden: false, value: "MOVIE", customClass: "" },
  sectionUrl,
  title = { hidden: false, value: "இந்திய அளவிலான கூடை பந்தாட்ட போட்டி - எந்த அணி", customClass: "" },
  timeAgo = { hidden: false, value: "30 min ago", customClass: "" },
  imageSrc = { hidden: false, value: "", customClass: "" },
  slug,
  priority = false,
  imageMetadata,
  templatetype = { hidden: false, value: "", customClass: "" },
}: Props) => {
  const categoryValue = category?.value || "";
  const titleValue = title?.value || "";
  const timeAgoValue = getTimeAgo(timeAgo?.value);
  const validImageSrc = getImageUrl(imageSrc?.value) || FALLBACK_IMAGE;
  const isLiveBlog = templatetype?.value === "live-blog" || templatetype?.value === "true";
  const router = useRouter();
  const sectionUrlValue = getSectionUrl(sectionUrl);

  const handleCategoryClick = (e: React.MouseEvent) => {
    if (sectionUrlValue) {
      e.preventDefault();
      e.stopPropagation();
      router.push(sectionUrlValue);
    }
  };

  const content = (
    <div className="flex flex-col bg-F8F8F8 overflow-hidden p-4 gap-4">
      {/* IMAGE */}
      {!imageSrc?.hidden && (
        <div>
          <div className="relative w-full aspect-[16/9] overflow-hidden">
            <Image
              src={validImageSrc}
              alt={titleValue}
              fill
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 33vw, 280px"
              className={`object-cover w-full h-full ${imageSrc?.customClass || ""}`}
              style={getFocusPointStyle(imageMetadata)}
              loading={priority ? "eager" : "lazy"}
              fetchPriority={priority ? "high" : "auto"}
              priority={priority}
              placeholder="blur"
              blurDataURL={BLUR_PLACEHOLDER}
            />
          </div>
        </div>
      )}

      {/* CONTENT */}
      <div className="flex flex-col gap-3">
        {/* Category */}
        {!category?.hidden && (
          sectionUrlValue ? (
            <span
              className={`text-11-tamil-600 text-012061 uppercase tracking-wide hover:underline cursor-pointer ${category?.customClass || ""}`}
              onClick={handleCategoryClick}
            >
              {categoryValue}
            </span>
          ) : (
            <p className={`text-11-tamil-600 text-012061 uppercase tracking-wide ${category?.customClass || ""}`}>
              {categoryValue}
            </p>
          )
        )}

        {/* Title */}
        {!title?.hidden && (
 <h3 className={`text-14-tamil-700 leading-[1.4] text-1B1B1B ${title?.customClass || ""}`}>
            {isLiveBlog && (
              <span className="inline-flex items-center mr-2 align-middle">
                <span className="relative inline-flex h-4 w-4 items-center justify-center">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-40 animate-[wave_1.5s_ease-out_infinite]" />
                  <span className="absolute inline-flex h-3 w-3 rounded-full bg-red-500 opacity-60 animate-[wave_1.5s_ease-out_0.3s_infinite]" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600" />
                </span>
              </span>
            )}
            {titleValue}
          </h3>
        )}

        {/* Footer */}
        <div className="flex justify-between items-center gap-3">
          {!timeAgo?.hidden && (
            <span className={`text-12-tamil-500 text-1B1B1B ${timeAgo?.customClass || ""}`}>{timeAgoValue}</span>
          )}
          <MessageIcon
            id="message-icon-1"
            slug={slug}
            category={BOOKMARK_CATEGORIES.NEWS}
            title={titleValue}
            thumbnailUrl={validImageSrc}
            articleUrl={slug ? `/${slug}` : undefined}
          />
        </div>
      </div>
    </div>
  );

  if (slug) {
    return (
      <Link href={`/${slug}`} className="block hover:bg-gray-100 transition-colors cursor-pointer">
        {content}
      </Link>
    );
  }

  return content;
};
